"use client";

import { PromoBanner } from "@/components/ui/promo-banner";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import Banner from "@/components/ui/banner";
import { HubSpotForm } from "@/components/ui/hubspot-form";
import { HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from "@/data/hubspot-forms";
import { HomeTestimonials } from "@/components/home/testimonials-wrapper";
import { TwoColumnFeatureSection } from "@/components/home/two-column-feature";
import { Check } from "lucide-react";

interface EstudioGratisLandingProps {
  headerFormId: string;
  footerFormId: string;
}

export function EstudioGratisLanding({ headerFormId, footerFormId }: EstudioGratisLandingProps) {
  const t = useTranslations("freeStudyPage");
  const benefits = t.raw("benefits.items") as Array<{ title: string; description: string }>;
  const [, setReviewMeta] = useState<{
    rating: number | null;
    count: number | null;
    googleMapsUrl?: string;
  }>({ rating: null, count: null });

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-surface-light text-ink dark:bg-surface-dark dark:text-gray-100">
      <PromoBanner variant="red" />
      {/* Hero — no nav escape buttons */}
      <Banner
        title={t("hero.title")}
        subtitle={t("hero.subtitle")}
        backgroundImage="/static/backgrounds/footer-background.webp"
      />

      {/* #presupuesto — header form */}
      <section id="presupuesto" className="scroll-mt-16 sm:scroll-mt-20 px-4 section-y">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="surface-card min-w-0 p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-label text-brand-red">
              {t("hero.eyebrow")}
            </p>
            <h2 className="mt-3 text-brand-navy dark:text-white type-h2">
              {t("hero.formTitle")}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-gray-700 dark:text-gray-300">
              {t("hero.formSubtitle")}
            </p>
            <div className="mt-6">
              <HubSpotForm
                portalId={HUBSPOT_PORTAL_ID}
                formId={headerFormId}
                region={HUBSPOT_REGION}
              />
            </div>
          </div>

          <div className="surface-card min-w-0 p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">
              {t("benefits.title")}
            </h3>
            <p className="mt-2 text-sm sm:text-base text-gray-700 dark:text-gray-300">
              {t("benefits.subtitle")}
            </p>
            <div className="mt-6 grid gap-4">
              {benefits.map((item) => (
                <div
                  key={item.title}
                  className="min-w-0 rounded-2xl bg-surface-light px-4 py-4 text-gray-700 shadow-sm dark:bg-surface-dark dark:text-gray-200"
                >
                  <p className="flex items-start gap-2 font-semibold text-brand-navy dark:text-white text-lg">
                    <Check className="w-5 h-5 text-green-500 mt-1" />
                    <span>{item.title}</span>
                  </p>
                  <p className="mt-1 text-gray-600 dark:text-gray-300 text-lg">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* #resenas — testimonials */}
      <div id="resenas" className="scroll-mt-16 sm:scroll-mt-20">
        <HomeTestimonials onMetaUpdate={setReviewMeta} />
      </div>

      {/* #nosotros — about / two-column feature */}
      <div id="nosotros" className="scroll-mt-16 sm:scroll-mt-20">
        <TwoColumnFeatureSection showCarousel />
      </div>

      {/* #contacto — footer form */}
      <section id="contacto" className="scroll-mt-16 sm:scroll-mt-20 bg-brand-navy px-4 section-y text-white dark:bg-surface-dark-deep">
        <div className="mx-auto max-w-5xl text-center">
          <h3 className="text-2xl sm:text-3xl font-black">{t("footerForm.title")}</h3>
          <p className="mt-3 text-base sm:text-lg text-mist">{t("footerForm.subtitle")}</p>
          <div className="hubspot-light mt-6 rounded-card bg-white p-6 sm:p-8 text-ink shadow-card-dark">
            <HubSpotForm
              portalId={HUBSPOT_PORTAL_ID}
              formId={footerFormId}
              region={HUBSPOT_REGION}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
